import React, { useEffect, useRef, useState } from 'react'
import { useApolloClient } from 'react-apollo'

import { getWindow } from '@acorns/web-utils'

import { closeSession, isAuthenticated } from 'utils/auth'
import { EnvironmentProps, withEnv } from 'utils/environment'
import { SessionHasExpiredModal } from './SessionHasExpiredModal'
import { SessionAboutToExpireModal } from './SessionAboutToExpireModal'
import { debounce } from 'lodash'

const ONE_SECOND_IN_MILLISECONDS = 1000

// The length of the session. This can be no longer than the expiration of either:
// - The expiration of the cookies storing the auth-related tokens.
// - The expiration of the auth-related tokens themselves on the backend.
export const SESSION_LENGTH = 10 * 60 * ONE_SECOND_IN_MILLISECONDS // 10 minutes in milliseconds

// The time to wait after session inactivity is detected before displaying "session about to expire" modal.
export const SESSION_ABOUT_TO_EXPIRE_WAIT_TIME = 8 * 60 * ONE_SECOND_IN_MILLISECONDS // 2 minutes in milliseconds

export const SessionInactivityDetection = withEnv(({ env }: EnvironmentProps) => {
  const apolloClient = useApolloClient()

  const [showSessionAboutToExpireModal, setShowSessionAboutToExpireModal] = useState(false)
  const [showSessionHasExpiredModal, setShowSessionHasExpiredModal] = useState(false)

  const sessionExpirationTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(function startSessionInactivityDetection() {
    const callback = debounce(() => {
      if (isAuthenticated()) {
        setShowSessionAboutToExpireModal(true)

        sessionExpirationTimeout.current = setTimeout(() => {
          setShowSessionHasExpiredModal(true)
        }, SESSION_LENGTH - SESSION_ABOUT_TO_EXPIRE_WAIT_TIME)
      }
    }, SESSION_ABOUT_TO_EXPIRE_WAIT_TIME)

    callback()

    window.document.addEventListener('mousemove', callback)
    window.document.addEventListener('touchstart', callback)

    return function cleanup() {
      window.document.removeEventListener('mousemove', callback)
      window.document.removeEventListener('touchstart', callback)
    }
  }, [])

  useEffect(
    function resetSessionExpirationTimeout() {
      if (showSessionAboutToExpireModal === false) {
        clearTimeout(sessionExpirationTimeout.current)
      }
    },
    [showSessionAboutToExpireModal],
  )

  useEffect(
    function handleShowSessionHasExpiredModal() {
      if (showSessionHasExpiredModal) {
        closeSession(apolloClient).then(() => {
          setTimeout(() => {
            getWindow().location.replace(env.get('loginUrl'))
          }, 5000)
        })
      }
    },
    [showSessionHasExpiredModal, apolloClient, env],
  )

  if (showSessionHasExpiredModal) {
    return <SessionHasExpiredModal />
  }

  if (showSessionAboutToExpireModal) {
    return (
      <SessionAboutToExpireModal
        setShowSessionAboutToExpireModal={setShowSessionAboutToExpireModal}
      />
    )
  }

  return null
})
